<template>
    <div>

        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0"><b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                @keyup.enter="applySearch" />
                            <b-input-group-append><b-button variant="info" @click="applySearch"><i
                                        class="mdi mdi-magnify"></i></b-button></b-input-group-append>
                        </b-input-group></div>
                    <div class="col-lg-8 d-flex justify-content-lg-end"><b-button variant="info" @click="openForm()">
                            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.product')]) }}
                        </b-button></div>
                </div>
                <div v-if="!status.list" class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
                <b-alert v-else-if="listError" show variant="danger">
                    {{ $t('label.productLoadError') }}
                    <b-button size="sm" variant="outline-danger" @click="loadProducts">{{ $t('label.retryPermissionDefinitions') }}</b-button>
                </b-alert>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive>
                        <template #cell(image)="{ item }">
                            <img v-if="item.image" :src="item.image" :alt="item.name" width="40" height="40" class="rounded product-image" />
                            <span v-else>-</span>
                        </template>
                        <template #cell(picUserName)="{ item }">{{ item.picUserName || '-' }}</template>
                        <template #cell(pdfUrl)="{ item }">
                            <a v-if="item.pdfUrl" :href="item.pdfUrl" target="_blank" rel="noopener noreferrer"
                                class="text-danger" v-b-tooltip.hover :title="$t('label.openPdf')"><i
                                    class="mdi mdi-file-pdf-box font-size-20"></i></a>
                            <span v-else>-</span>
                        </template>
                        <template #cell(isActive)="{ item }">
                            <b-form-checkbox :key="`${item.uuid}-${statusToggleVersion}`" :checked="item.isActive" switch
                                :disabled="!status.save || statusTogglePending" @change="confirmStatus(item, $event)">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-form-checkbox>
                        </template>
                        <template #cell(actions)="{ item }">
                            <a href="javascript:void(0);" class="text-info" @click.stop="openForm(item)"><i
                                    class="mdi mdi-pencil-outline font-size-18"></i></a>
                            <a href="javascript:void(0);" class="text-danger ml-2" @click.stop="confirmDelete(item)"><i
                                    class="mdi mdi-delete-outline font-size-18"></i></a>
                        </template>
                    </b-table>
                    <div v-if="pagination.total" class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">{{ $tc('label.results', pagination.total, [pagination.total])
                        }}</small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="product-form" :title="$t(product.uuid ? 'label.updateX' : 'label.createX', [$t('module.product')])"
            size="xl" hide-footer hide-header-close centered @hidden="closeForm"><b-container fluid>
                <ProductForm v-if="formVisible" :product-u-u-i-d="product.uuid" :search-criteria="searchCriteria"
                    @done="closeForm" />
            </b-container></b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import i18n from '@/i18n';
import { productMethods } from '@/state/api';
import { PRODUCT_MODULE } from '@/state/api/product.state';
import ProductForm from '@/views/pages/admin/product/form';

const defaultCriteria = () => ({ pageNumber: 1, pageSize: 15, searchArray: [] });

export default {
    name: 'ProductListing', components: { ProductForm },
    data() {
        return {
            statusToggleVersion: 0, statusTogglePending: false,
            searchText: '', currentPage: 1, formVisible: false, product: { uuid: '' }, searchCriteria: defaultCriteria(),
            fields: [
                { key: 'image', label: i18n.t('label.image'), thStyle: { width: '64px' }, tdClass: 'product-image-cell' },
                { key: 'name', label: i18n.t('label.name') },
                { key: 'categoryName', label: i18n.t('module.productCategory') },
                { key: 'conditionName', label: i18n.t('module.productCondition') },
                { key: 'picUserName', label: i18n.t('label.picUser'), sortable: false },
                { key: 'pdfUrl', label: i18n.t('label.pdf'), sortable: false },
                { key: 'isActive', label: i18n.t('label.status'), sortable: false },
                { key: 'actions', label: i18n.t('label.action'), sortable: false },
            ],
        };
    },
    computed: {
        ...mapState(PRODUCT_MODULE, ['status', 'listError']),
        tableRows() {
            return this.$store.state.api.product.list.data || [];
        },
        pagination() {
            return this.$store.state.api.product.list.pagination || {};
        },
    },
    created() { this.loadProducts(); },
    methods: {
        ...productMethods,
        loadProducts() {
            return this.getAllProduct({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1; this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : []
            }; this.loadProducts();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadProducts();
        },
        openForm(product = null) {
            this.product = { uuid: product?.uuid || '' }; this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('product-form'));
        },
        closeForm() {
            this.$bvModal.hide('product-form'); this.formVisible = false; this.product = { uuid: '' };
        },
        async confirmStatus(product, isActive) {
            if (this.statusTogglePending || !this.status.save) return;
            this.statusTogglePending = true;
            try {
                const action = this.$t(isActive ? 'label.activate' : 'label.deactivate');
                const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('label.confirmChangeStatusX', [action, this.$t('module.product')]),
                    { title: this.$t('label.confirmStatusChange'), okTitle: this.$t('label.confirm'), okVariant: 'info', cancelTitle: this.$t('label.cancel'), centered: true });
                if (confirmed) await this.updateProductStatus({ uuid: product.uuid, isActive, filter: this.searchCriteria });
            } finally {
                // Restore the saved status after cancellation or a failed request.
                this.statusToggleVersion++;
                this.statusTogglePending = false;
            }
        },
        async confirmDelete(product) {
            const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('label.confirmDeleteX', [this.$t('module.product')]),
                { title: this.$t('label.delete'), okTitle: this.$t('label.delete'), okVariant: 'danger', cancelTitle: this.$t('label.cancel'), centered: true });
            if (confirmed) {
                const result = await this.deleteProduct({ uuid: product.uuid, filter: this.searchCriteria });
                if (result?.success && this.currentPage > 1 && !this.tableRows.length) {
                    this.pageNavigation(this.currentPage - 1);
                }
            }
        },
    },
};
</script>

<style scoped>
.product-image { object-fit: cover; }
::v-deep .product-image-cell { width: 64px; padding: 0.5rem; }
</style>
